import React from "react";

/**
 * FxPanel — master FX unit: Echo / Filter / Flanger + amount fader.
 * Per-deck SEND toggles let the host route FX to ONLY one deck at a time
 * (e.g. flanger-sweep into the next track on Deck B while Deck A stays clean).
 * Extracted from RadioHostStudio so the page file stays under control.
 */
export default function FxPanel({
  fxType, setFxType, fxAmount, setFxAmount,
  fxSendA = true, fxSendB = true,
  setFxSendA, setFxSendB,
}) {
  const types = [
    { key: "off", label: "Off", icon: "✕" },
    { key: "echo", label: "Echo", icon: "🔁" },
    { key: "filter", label: "Filter", icon: "🎚" },
    { key: "flanger", label: "Flanger", icon: "〰︎" },
  ];
  // Compact label that summarises the routing at a glance.
  const routeLabel = fxSendA && fxSendB ? "Both" : fxSendA ? "Deck A only" : fxSendB ? "Deck B only" : "Bypassed";
  return (
    <div data-testid="radio-host-fx-unit" className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="text-white font-black text-[11px] uppercase tracking-wider flex-1">Master FX</div>
        {fxType !== "off" && fxAmount > 0.02 && (
          <span className="inline-flex items-center gap-1 bg-fuchsia-500/25 text-fuchsia-100 border border-fuchsia-500/40 text-[9px] font-black px-1.5 py-0.5 rounded animate-pulse">WET · {Math.round(fxAmount * 100)}%</span>
        )}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {types.map((t) => (
          <button
            key={t.key}
            data-testid={`radio-host-fx-type-${t.key}`}
            onClick={() => setFxType(t.key)}
            className={`font-black text-[10px] py-1.5 rounded border ${fxType === t.key ? "bg-fuchsia-500 text-black border-fuchsia-400" : "bg-white/5 text-white/70 hover:bg-white/15 border-white/10"}`}
          >
            <div className="leading-none">{t.icon}</div>
            <div className="mt-0.5">{t.label}</div>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 px-1">
        <span className="text-[9px] uppercase tracking-wider text-white/50 font-black">DRY</span>
        <input
          data-testid="radio-host-fx-amount"
          type="range" min="0" max="1" step="0.01"
          value={fxAmount}
          onChange={(e) => setFxAmount(parseFloat(e.target.value))}
          disabled={fxType === "off"}
          className="flex-1 accent-fuchsia-500 disabled:opacity-40 h-1.5"
        />
        <span className="text-[9px] uppercase tracking-wider text-white/50 font-black">WET</span>
      </div>
      {/* Per-deck FX-send toggles — flip which deck the FX bus is fed from. */}
      <div className="flex items-center gap-1.5 pt-1">
        <span className="text-[9px] uppercase tracking-wider text-white/50 font-black mr-1">Send</span>
        <button
          data-testid="radio-host-fx-send-a"
          onClick={() => setFxSendA?.((v) => !v)}
          aria-pressed={fxSendA}
          className={`flex-1 text-[10px] font-black px-2 py-1 rounded border transition ${fxSendA ? "bg-sky-500/25 text-sky-100 border-sky-500/50" : "bg-white/5 text-white/40 border-white/10 hover:bg-white/15"}`}
          title="Route FX to Deck A"
        >Deck A {fxSendA ? "ON" : "off"}</button>
        <button
          data-testid="radio-host-fx-send-b"
          onClick={() => setFxSendB?.((v) => !v)}
          aria-pressed={fxSendB}
          className={`flex-1 text-[10px] font-black px-2 py-1 rounded border transition ${fxSendB ? "bg-rose-500/25 text-rose-100 border-rose-500/50" : "bg-white/5 text-white/40 border-white/10 hover:bg-white/15"}`}
          title="Route FX to Deck B"
        >Deck B {fxSendB ? "ON" : "off"}</button>
      </div>
      <div className="text-white/40 text-[9px] italic">
        {fxType === "off"
          ? "Pick an effect to send through. Toggle Deck A / B to choose what's affected."
          : `Routed to: ${routeLabel}`}
      </div>
    </div>
  );
}
